import {
    ButtonFlavor,
    ButtonType,
    ExButton,
    ExEmptyState,
    ExIcon,
    ExLoader,
    IconSize,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { FABRIC_BASE_URL } from '../../config';
import { GENERIC_ERROR_MESSAGE } from '../../shared/apiUtils';
import ErrorAlertBanner from '../../shared/ErrorAlertBanner';
import IdentityProviderDrawer from '../IdentityProviderDrawer';
import { fetchIdentityProviders, type IdentityProvider } from '../identityProviders';
import { useIdentityProviderDrawer } from '../useIdentityProviderDrawer';
import IdentityProviderCard from './IdentityProviderCard';
import styles from './IdentityProviderGrid.module.css';

const IdentityProviderGrid = () => {
    const [items, setItems] = useState<IdentityProvider[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const { drawerMode, openCreate, openEdit, navigateToBase } = useIdentityProviderDrawer();

    // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey bump on drawer close re-runs the fetch
    useEffect(() => {
        setLoading(true);
        fetchIdentityProviders()
            .then((response) => setItems(response.results))
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [refreshKey]);

    const handleDrawerClose = () => {
        setRefreshKey((k) => k + 1);
        navigateToBase();
    };

    return (
        <section>
            <div className={styles.header}>
                <h2 className={`${styles.heading} ex-text-h2`}>Identity Providers</h2>
            </div>
            {error ? (
                <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>
            ) : items.length === 0 ? (
                loading ? (
                    <ExLoader label="Loading identity providers…" />
                ) : (
                    <ExEmptyState
                        className={styles.empty}
                        label="No identity providers configured"
                        text="Add an identity provider to authenticate connections to your connectors."
                    >
                        <div slot="header">
                            <img
                                src={`${FABRIC_BASE_URL}/emptyStateIcon.svg`}
                                alt="No identity providers configured"
                            />
                        </div>
                        <ExButton
                            data-pendo-button="idp-emptystate-createidp"
                            slot="action"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={openCreate}
                        >
                            Create Identity Provider
                        </ExButton>
                    </ExEmptyState>
                )
            ) : (
                <div className={styles.grid}>
                    {items.map((idp) => (
                        <IdentityProviderCard key={idp.id} idp={idp} onSelect={openEdit} />
                    ))}
                    {items.length > 0 && (
                        <button
                            type="button"
                            className={styles.cardAdd}
                            onClick={openCreate}
                            data-pendo-button="idp-cardgrid-createidp"
                        >
                            <ExIcon className={styles.addIcon} icon="Add new" size={IconSize.L} />
                            <span className={styles.addTitle}>Add Identity Provider</span>
                            <span className={styles.addDescription}>
                                Connect Okta, Azure AD, Auth0, Keycloak or custom OIDC/SAML
                            </span>
                        </button>
                    )}
                </div>
            )}
            <IdentityProviderDrawer mode={drawerMode} onClose={handleDrawerClose} />
        </section>
    );
};

export default IdentityProviderGrid;
